import React, { useState, useEffect, useMemo } from 'react';
import apiService from '../services/ApiService';

const ServiceRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [viewRequest, setViewRequest] = useState(null);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [comment, setComment] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  const itemsPerPage = 10;

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const result = await apiService.get('/service-requests');
      if (result.status === 'success') {
        setRequests(result.data || []);
      }
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to fetch service requests');
    } finally {
      setLoading(false);
    }
  };

  const filteredRequests = useMemo(() => {
    return requests.filter(req => {
      const matchesStatus = statusFilter === 'all' || req.status === statusFilter;
      const additionalInfo = JSON.parse(req.additional_info || '{}');
      const searchableText = [
        req.request_id,
        req.service_type,
        req.vehicle_id?.vehicle_number,
        req.vehicle_id?.brand,
        req.vehicle_id?.model,
        ...Object.values(additionalInfo)
      ].join(' ').toLowerCase();

      const matchesSearch = searchableText.includes(searchQuery.toLowerCase());
      return matchesStatus && matchesSearch;
    });
  }, [requests, searchQuery, statusFilter]);

  const paginatedRequests = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredRequests.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredRequests, currentPage]);

  const totalPages = Math.ceil(filteredRequests.length / itemsPerPage);

  const handleUpdateClick = (req) => {
    setSelectedRequest(req);
    setNewStatus(req.status);
    setComment('');
    setIsUpdateModalOpen(true);
  };

  const handleStatusUpdate = async (e) => {
    e.preventDefault();
    if (!newStatus || !comment.trim()) {
      alert('Please provide both status and comment');
      return;
    }
    setIsProcessing(true);
    try {
      await apiService.patch(`/service-requests/${selectedRequest.request_id}`, {
        status: newStatus,
        comment: comment.trim()
      });
      await fetchRequests();
      setIsUpdateModalOpen(false);
    } catch (err) {
      alert(err.message || 'Failed to update request');
    } finally {
      setIsProcessing(false);
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'completed': return 'status-completed';
      case 'pending': return 'status-pending';
      case 'rejected': return 'status-rejected';
      case 'in_progress': return 'status-in_progress';
      default: return '';
    }
  };

  if (loading && requests.length === 0) return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '100px' }}>
      <i className="fas fa-circle-notch fa-spin" style={{ fontSize: '30px', color: 'var(--primary)' }}></i>
    </div>
  );

  return (
    <div className="requests-page">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '32px', gap: '20px', flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ fontFamily: 'Outfit', fontSize: '24px', fontWeight: '800', margin: '0', color: 'var(--text-main)' }}>Service Requests</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginTop: '4px' }}>Monitor and manage specialized RTO operations and tracking.</p>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <div className="filter-pills" style={{ display: 'flex', background: 'var(--bg-main)', padding: '4px', borderRadius: '12px', border: '1px solid var(--border-light)' }}>
            {['all', 'pending', 'in_progress', 'completed'].map(f => (
              <button
                key={f}
                onClick={() => setStatusFilter(f)}
                style={{
                  padding: '8px 16px',
                  borderRadius: '10px',
                  border: 'none',
                  fontSize: '12px',
                  fontWeight: '700',
                  textTransform: 'uppercase',
                  cursor: 'pointer',
                  background: statusFilter === f ? 'white' : 'transparent',
                  color: statusFilter === f ? 'var(--primary)' : 'var(--text-dim)',
                  boxShadow: statusFilter === f ? 'var(--shadow-sm)' : 'none',
                  transition: 'all 0.2s'
                }}
              >
                {f.replace('_', ' ')}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="admin-card" style={{ padding: '0', overflow: 'hidden' }}>
        <div style={{ padding: '24px', borderBottom: '1px solid var(--border-light)', background: 'var(--bg-main)' }}>
          <div style={{ position: 'relative' }}>
            <i className="fas fa-search" style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-dim)' }}></i>
            <input
              type="text"
              placeholder="Search by ID, Vehicle Number or Service Category..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 12px 12px 48px',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--border-light)',
                fontSize: '14px',
                background: 'white',
                outline: 'none',
                boxSizing: 'border-box'
              }}
            />
          </div>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--bg-main)', textAlign: 'left' }}>
                <th style={tableHeaderStyle}>TRACKING ID</th>
                <th style={tableHeaderStyle} className="hide-mobile">ASSET & OWNER</th>
                <th style={tableHeaderStyle}>SERVICE TYPE</th>
                <th style={tableHeaderStyle}>STATUS</th>
                <th style={tableHeaderStyle}>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {paginatedRequests.length > 0 ? paginatedRequests.map(req => (
                <tr key={req.request_id} style={{
                  borderBottom: '1px solid var(--border-light)',
                  transition: 'background 0.2s'
                }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--primary-light)'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                  <td style={tableCellStyle}>
                    <div style={{ fontWeight: '800', color: 'var(--primary)', fontFamily: 'Inter', fontSize: '13px' }}>
                      #{req.request_id.slice(-8).toUpperCase()}
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--text-dim)', marginTop: '4px', fontWeight: '600' }}>
                      {new Date(req.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </div>
                  </td>
                  <td style={tableCellStyle} className="hide-mobile">
                    {req.vehicle_id ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{ width: '32px', height: '32px', background: 'var(--bg-main)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-main)', fontSize: '12px' }}>
                          <i className="fas fa-car-side"></i>
                        </div>
                        <div>
                          <div style={{ fontWeight: '700' }}>{req.vehicle_id.vehicle_number}</div>
                          <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{req.vehicle_id.brand} {req.vehicle_id.model}</div>
                        </div>
                      </div>
                    ) : (
                      <span style={{ color: 'var(--text-dim)', fontSize: '12px' }}>Ghost Request</span>
                    )}
                  </td>
                  <td style={tableCellStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'var(--primary)' }}></span>
                      <span style={{ fontWeight: '700', fontSize: '13px' }}>{req.service_type}</span>
                    </div>
                  </td>
                  <td style={tableCellStyle}>
                    <span className={`status-badge ${getStatusClass(req.status)}`} style={{ fontSize: '11px' }}>
                      {req.status.replace('_', ' ')}
                    </span>
                  </td>
                  <td style={tableCellStyle}>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button className="btn btn-outline" style={{ padding: '8px 12px', fontSize: '12px' }} onClick={() => setViewRequest(req)}>
                        <i className="fas fa-eye"></i> Details
                      </button>
                      <button className="btn btn-primary" style={{ padding: '8px 12px', fontSize: '12px' }} onClick={() => handleUpdateClick(req)}>
                        <i className="fas fa-sync-alt"></i> Update
                      </button>
                    </div>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="5" style={{ padding: '64px', textAlign: 'center' }}>
                    <div style={{ color: 'var(--text-dim)', fontSize: '48px', marginBottom: '16px', opacity: 0.2 }}>
                      <i className="fas fa-clipboard-list"></i>
                    </div>
                    <p style={{ color: 'var(--text-muted)', fontWeight: '600' }}>No service requests found.</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div style={{ padding: '24px', borderTop: '1px solid var(--border-light)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--bg-main)' }}>
          <span style={{ fontSize: '13px', color: 'var(--text-muted)', fontWeight: '600' }}>
            Page {currentPage} of {Math.max(1, totalPages)}
          </span>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button className="btn btn-outline" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>
              <i className="fas fa-arrow-left"></i>
            </button>
            <button className="btn btn-outline" disabled={currentPage === totalPages || totalPages === 0} onClick={() => setCurrentPage(p => p + 1)}>
              <i className="fas fa-arrow-right"></i>
            </button>
          </div>
        </div>
      </div>

      {/* View Detail Modal */}
      {viewRequest && (
        <div className="modal-overlay" onClick={() => setViewRequest(null)}>
          <div className="admin-card" style={{ maxWidth: '900px', width: '95%', padding: '0', overflow: 'hidden', border: 'none' }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: '32px', background: 'var(--grad-primary)', color: 'white', position: 'relative' }}>
              <div style={{ position: 'relative', zIndex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                    <span style={{ background: 'rgba(255,255,255,0.2)', padding: '4px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: '800' }}>#{viewRequest.request_id.toUpperCase()}</span>
                    <span className={`status-badge ${getStatusClass(viewRequest.status)}`} style={{ border: '1px solid rgba(255,255,255,0.3)' }}>{viewRequest.status}</span>
                  </div>
                  <h3 style={{ margin: 0, fontSize: '28px', fontWeight: '800' }}>{viewRequest.service_type}</h3>
                  <p style={{ margin: '8px 0 0', opacity: 0.8, fontSize: '14px' }}>
                    Submitted on {new Date(viewRequest.createdAt).toLocaleString()}
                  </p>
                </div>
                <button style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', cursor: 'pointer', width: '40px', height: '40px', borderRadius: '50%', fontSize: '20px' }} onClick={() => setViewRequest(null)}>×</button>
              </div>
            </div>

            <div style={{ padding: '32px', maxHeight: '70vh', overflowY: 'auto' }} className="grid-responsive">
              <div className="col-7 col-lg-12">
                <section style={{ marginBottom: '32px' }}>
                  <h4 style={sectionHeaderStyle}>Submission Data</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', background: 'var(--bg-main)', padding: '20px', borderRadius: '16px' }}>
                    {Object.entries(JSON.parse(viewRequest.additional_info || '{}')).map(([key, val]) => (
                      <div key={key}>
                        <div style={{ fontSize: '10px', color: 'var(--text-dim)', fontWeight: '800', textTransform: 'uppercase', marginBottom: '4px' }}>{key.replace(/_/g, ' ')}</div>
                        <div style={{ fontSize: '14px', fontWeight: '700', color: 'var(--text-main)' }}>{String(val) || 'N/A'}</div>
                      </div>
                    ))}
                  </div>
                </section>

                {viewRequest.documents?.length > 0 && (
                  <section>
                    <h4 style={sectionHeaderStyle}>Attached Evidence</h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {viewRequest.documents.map(doc => (
                        <a key={doc.document_id} href={doc.file_path} target="_blank" rel="noreferrer"
                          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px', background: 'white', border: '1px solid var(--border-light)', borderRadius: '12px', textDecoration: 'none', color: 'inherit' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div style={{ width: '36px', height: '36px', background: 'var(--error-light)', color: 'var(--error)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <i className="fas fa-file-pdf"></i>
                            </div>
                            <div>
                              <div style={{ fontWeight: '700', fontSize: '13px' }}>{doc.document_type}</div>
                              <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{doc.file_name}</div>
                            </div>
                          </div>
                          <i className="fas fa-external-link-alt" style={{ fontSize: '12px', color: 'var(--text-dim)' }}></i>
                        </a>
                      ))}
                    </div>
                  </section>
                )}
              </div>

              <div className="col-5 col-lg-12">
                <h4 style={sectionHeaderStyle}>Audit Log & Timeline</h4>
                <div style={{ position: 'relative', paddingLeft: '24px' }}>
                  <div style={{ position: 'absolute', left: '0', top: '0', bottom: '0', width: '2px', background: 'var(--border-light)', borderRadius: '1px' }}></div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                    {viewRequest.comments?.length > 0 ? [...viewRequest.comments].reverse().map((c, i) => (
                      <div key={i} style={{ position: 'relative' }}>
                        <div style={{ position: 'absolute', left: '-29px', top: '4px', width: '12px', height: '12px', borderRadius: '50%', background: 'white', border: '3px solid var(--primary)', zIndex: 1 }}></div>
                        <div style={{ background: 'var(--bg-main)', padding: '16px', borderRadius: '14px', border: '1px solid var(--border-light)' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                            <span style={{ fontSize: '11px', fontWeight: '800', color: 'var(--primary)', textTransform: 'uppercase' }}>System Update</span>
                            <span style={{ fontSize: '11px', color: 'var(--text-dim)' }}>{new Date(c.createdAt).toLocaleString()}</span>
                          </div>
                          <p style={{ margin: 0, fontSize: '13px', lineHeight: '1.5', color: 'var(--text-main)', fontWeight: '600' }}>{c.text}</p>
                        </div>
                      </div>
                    )) : (
                      <div style={{ color: 'var(--text-dim)', fontSize: '13px', padding: '20px', textAlign: 'center' }}>
                        No interaction history for this request.
                      </div>
                    )}

                    <div style={{ position: 'relative' }}>
                      <div style={{ position: 'absolute', left: '-29px', top: '4px', width: '12px', height: '12px', borderRadius: '50%', background: 'white', border: '3px solid var(--border-light)', zIndex: 1 }}></div>
                      <div style={{ padding: '0 16px', fontSize: '12px', color: 'var(--text-dim)', fontWeight: '700' }}>
                        Request Initialized
                      </div>
                    </div>
                  </div>
                </div>

                <button
                  className="btn btn-primary"
                  style={{ width: '100%', marginTop: '32px', padding: '14px' }}
                  onClick={() => {
                    handleUpdateClick(viewRequest);
                    setViewRequest(null);
                  }}
                >
                  Post Status Update
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Update Status Modal */}
      {isUpdateModalOpen && (
        <div className="modal-overlay">
          <div className="admin-card" style={{ maxWidth: '500px', width: '90%', padding: '0', overflow: 'hidden', border: 'none' }}>
            <div style={{ padding: '24px', background: 'var(--grad-primary)', color: 'white', textAlign: 'center' }}>
              <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '800' }}>Change Request Status</h3>
              <p style={{ margin: '4px 0 0', opacity: 0.8, fontSize: '12px' }}>Tracking ID: #{selectedRequest?.request_id.toUpperCase()}</p>
            </div>

            <form onSubmit={handleStatusUpdate} style={{ padding: '24px' }}>
              <div className="form-group" style={{ marginBottom: '20px' }}>
                <label style={labelStyle}>SELECT NEW STATE</label>
                <select style={inputStyle} value={newStatus} onChange={e => setNewStatus(e.target.value)}>
                  <option value="pending">🟡 Pending (New)</option>
                  <option value="in_progress">🔵 In Progress (Working)</option>
                  <option value="completed">🟢 Completed (Successful)</option>
                  <option value="rejected">🔴 Rejected (Disapproved)</option>
                </select>
              </div>
              <div className="form-group">
                <label style={labelStyle}>INTERNAL AUDIT COMMENT</label>
                <textarea
                  rows="4"
                  style={{ ...inputStyle, resize: 'none' }}
                  value={comment}
                  onChange={e => setComment(e.target.value)}
                  placeholder="Explain the reason for this status change or provide instructions for the user..."
                />
              </div>
              <div style={{ display: 'flex', gap: '12px', marginTop: '32px' }}>
                <button type="button" className="btn btn-outline" style={{ flex: 1 }} onClick={() => setIsUpdateModalOpen(false)}>Discard</button>
                <button type="submit" className="btn btn-primary" style={{ flex: 2 }} disabled={isProcessing}>
                  {isProcessing ? <i className="fas fa-spinner fa-spin"></i> : 'Publish Update'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        .modal-overlay { position: fixed; inset: 0; background: rgba(15, 23, 42, 0.7); backdrop-filter: blur(8px); display: flex; align-items: center; justify-content: center; z-index: 2000; padding: 20px; }
      `}</style>
    </div>
  );
};

const sectionHeaderStyle = {
  fontSize: '11px',
  fontWeight: '800',
  color: 'var(--text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '1.5px',
  marginBottom: '16px',
  display: 'flex',
  alignItems: 'center',
  gap: '8px'
};

const tableHeaderStyle = {
  padding: '16px 24px',
  color: 'var(--text-muted)',
  fontSize: '11px',
  fontWeight: '800',
  letterSpacing: '1px',
  borderBottom: '1px solid var(--border-light)'
};

const tableCellStyle = {
  padding: '20px 24px',
  fontSize: '14px',
  color: 'var(--text-main)'
};

const labelStyle = {
  display: 'block',
  marginBottom: '8px',
  fontWeight: '800',
  fontSize: '11px',
  color: 'var(--text-dim)',
  letterSpacing: '1px'
};

const inputStyle = {
  width: '100%',
  padding: '14px',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border-light)',
  fontSize: '14px',
  outline: 'none',
  boxSizing: 'border-box',
  background: '#fcfcfc',
  transition: 'all 0.2s',
  fontFamily: 'inherit'
};

export default ServiceRequests;
